import React, { useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import Overview, { OverviewProps } from './Overview';
import Option from './Option';
import Box from '@ff/ui/Box';
import { styled } from '@ff/ui';
import Summary from './Summary';
import useWindowSize from 'client/hooks/useWindowSize';
import constants from '../../../constants';
import modelCodes from 'client/constants/modelCode';
import { HomeCharger, PublicChargingCredit, FFPortableChargingCable } from './OtherOptions';

const {
  HEADER_HEIGHT,
  CHECK_CENTERED_OPTION_VIEW_HEIGHT
} = constants;

type OptionProps = {
  code: string,
  name: string;
  productImageScaleRatio?: number;
  productImagePosition?: 'center' | 'top' | 'bottom' | 'left' | 'right';
  items: {
    code: string;
    image: string;
    productImage: string;
    title: string;
    features?: string[];
    price?: number;
  }[],
  selectedItemCode: string;
}

type SummaryProps = {
  modelCode: string;
  basePrice: number;
  totalPrice: number;
  summaryPageUrl: string;
  disableButton: boolean;
  exterior: {
    code: string;
    name: string;
    price: number
  },
  wheels: {
    code: string;
    name: string;
    price: number
  },
  interior: {
    code: string;
    name: string;
    price: number;
  },
}

export type OptionsSidebarProps = {
  modelCode: string,
  overview: OverviewProps;
  exterior: OptionProps;
  wheels: OptionProps;
  interior: OptionProps;
  summary: SummaryProps;
  allowGotoModelSelection: boolean;
  onOptionsChanged: (optionsCode: string, selectedOptionItemCode: string) => void;
  onCenterOptionChanged: (optionCode: string) => void;
}

export default function OptionsSidebar({
  modelCode,
  overview,
  exterior,
  wheels,
  interior,
  summary,
  onOptionsChanged,
  onCenterOptionChanged,
}: OptionsSidebarProps) {
  const [centerOption, setCenterOption] = useState();
  const { height: windowHeight} = useWindowSize();
  const viewMarginTop = windowHeight && ((windowHeight - HEADER_HEIGHT) / 2) - CHECK_CENTERED_OPTION_VIEW_HEIGHT / 2 + HEADER_HEIGHT;
  const viewMarginBottom = windowHeight && ((windowHeight - HEADER_HEIGHT) / 2) - CHECK_CENTERED_OPTION_VIEW_HEIGHT / 2;
  const rootMargin = `-${viewMarginTop}px 0px -${viewMarginBottom}px 0px`;
  const rootMarginOfLastOption = `600px 0px 10px 0px`; // the 600px should be greater than the height of last option

  const [isShowSummary, setIsShowSummary] = useState(false);
  const refSummary = useRef<HTMLDivElement>(null);

  const handleInViewChanged = (optionCode, inView) => {
    if(inView && optionCode !== centerOption){
      setCenterOption(optionCode);
      onCenterOptionChanged(optionCode);
    }
  };

  const handleLastOptionInViewChanged = (inView) => {
    setIsShowSummary(inView)
  }

  const [refExt] = useInView({
    threshold: [0.01],
    rootMargin,
    onChange: (iv) => { handleInViewChanged(exterior.code, iv) }
  });
  const [refWhl] = useInView({
    threshold: [0.01],
    rootMargin,
    onChange: (iv) => { handleInViewChanged(wheels.code, iv) }
  });
  const [refInt] = useInView({
    threshold: [0.01],
    rootMargin,
    onChange: (iv) => { handleInViewChanged(interior.code, iv) }
  });

  const [refLastOption] = useInView({
    threshold: [0.9, 1],
    rootMargin: rootMarginOfLastOption,
    onChange: (iv) => { handleLastOptionInViewChanged(iv) }
  });

  const handleOptionsChanged = (optionName: string, selectedOptionItemCode: string) => {
    onOptionsChanged(optionName, selectedOptionItemCode);
  }

  // Fix lint - comment for now
  // const chargingYourFF91Content = (
  //   <OptionModal
  //     title='Charging Your FF 91'
  //     items={[
  //       {
  //         subTitle: 'Description',
  //         descriptions: [
  //           'Included with every FF 91',
  //           'Flexible for Level 1 - Level 2 charging',
  //           '40 Amps, 240 volt charger',
  //           'Adds up to 30 miles of range/hour'
  //         ]
  //       },
  //     ]}
  //     images={[
  //       `${url.getCDNDomainV2()}/configurator/design/images/other/FF91_Sidebar_Option_PortalCharger_Content_1.png`
  //     ]}
  //   />
  // );

  return (
    <Container>
      <OptionsContainer>
        <OverviewContainer>
          <Overview {...overview}></Overview>
        </OverviewContainer>
        <Separation />
        <Option {...exterior} onOptionChanged={handleOptionsChanged} ref={refExt}></Option>
        <Separation />
        <Option {...wheels} onOptionChanged={handleOptionsChanged} ref={refWhl}></Option>
        <Separation />
        <Option {...interior} onOptionChanged={handleOptionsChanged} ref={refInt}></Option>
        <Separation />
        <ContainerWithPadding>
          { modelCode === modelCodes.FF91FuturistAlliance.fullModelCode && (
            <>
              <HomeCharger />
              <Separation />
            </>
          )} 
          <PublicChargingCredit />
          <Separation />
          <FFPortableChargingCable />
          <LastSeparation ref={refLastOption} />
        </ContainerWithPadding>
      </OptionsContainer>
      <SummaryContainer ref={refSummary} isShown={isShowSummary}>
        <Summary {...{...summary, visible: isShowSummary}} />
      </SummaryContainer>
    </Container>
  );
}

const Container = styled(Box)({
  width: 'inherit',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '66px',
});

const OptionsContainer = styled(Box)(({ theme }) => ({
  width: 'inherit',
  display: 'flex',
  flexDirection: 'column',

  [theme.mediaQueries.md]: {
    minWidth: '400px',
  }
}));

const ContainerWithPadding = styled(Box)(({ theme }) => ({
  paddingLeft: '20px',
  paddingRight: '20px',

  [theme.mediaQueries.md]: {
    paddingLeft: '48px',
    paddingRight: '48px',
  }
}))

const Separation = styled(Box)(({ theme }) => ({
  height: '100px',

  [theme.mediaQueries.md]: {
    height: '20vh',
  }
}));

const LastSeparation = styled(Box)(({ theme }) => ({
  height: '300px',

  [theme.mediaQueries.md]: {
    minHeight: '300px',
    height: '20vh',
  }
}));

const OverviewContainer = styled(ContainerWithPadding)(({ theme }) => ({
  marginTop: '24px',
  [theme.mediaQueries.md]: {
    marginTop: '48px',
  }
}));

const SummaryContainer = styled(Box)<{ isShown: boolean }>(({ isShown }) => ({
  position: 'fixed',
  overflow: 'hidden',
  width: 'inherit',
  opacity: isShown ? 1 : 0,
  visibility: isShown ? 'visible' : 'hidden',
  transition: 'all 0.6s ease-in',
  bottom: isShown ? '0' : '-203px',
  right: '0px',
}))
